import { NumberInputField } from "@src/components/deprecated/NumberInputField/NumberInputField";
import { NumberFieldFormat, NumberUtilities, RawNumberFormat } from "@utils/numberUtilities";
import { t } from "i18next";
import { DataObjectFieldComparator } from "../../DataObjects/DataObjectFieldComparator";
import { DataObjectType } from "../../DataObjects/DataObjectHelper";
import { DataObjectField, InputElementContext } from "../DataObjectField";
import styles from "../DataObjectField.module.css";

export class FirebaseDataNumberField<FirebaseDataType extends DataObjectType> extends DataObjectField<number, FirebaseDataType> {
  metric: NumberFieldFormat | undefined;
  rawDataMetric: RawNumberFormat | undefined;
  decimals: number | undefined;

  constructor(
    key: string,
    title: string,
    dataGetter: (data: FirebaseDataType) => number | number[] | undefined,
    iconName?: string,
    isArray: boolean = false,
    settings: { metric?: NumberFieldFormat; rawDataMetric?: RawNumberFormat; decimals?: number } = {}
  ) {
    super(key, title, dataGetter, iconName, isArray);
    this.metric = settings.metric;
    this.rawDataMetric = settings.rawDataMetric;
    this.decimals = settings.decimals;
  }
  singleInputElement = (value: number | undefined, setValue: (value: number | undefined) => void, context?: InputElementContext) => {
    const metric = this.getMetricLabel();
    return (
      <NumberInputField
        value={value}
        onChange={(number) => setValue(number)}
        placeholder={t("main.fillInANumber")}
        className={styles.textField}
        trailingText={metric}
        width={context?.width}
        disabled={context?.disabled}
      />
    );
  };

  private getMetricLabel = (): string => {
    if (this.metric === "currency") return "NOK";
    if (this.metric === "sqm") return "m²";
    if (this.metric === "percentage") return "%";
    if (this.metric === "hours") return t("main.hours");
    if (this.metric === "days") return t("main.days");
    if (this.metric === "months") return t("main.months");
    if (this.metric === "years") return t("main.years");
    return "";
  };

  override getReadableStringRepresentation(value: number): string {
    return NumberUtilities.formatValueToString(value, this.metric, this.rawDataMetric, this.decimals);
  }

  encodeString = (value: number) => value.toString();
  decodeString = (value: string) => {
    value = value.replace(",", ".");
    value = value.replace(" ", "");

    const isNumber = /^[0-9]+([,.][0-9]+)?$/.test(value);
    if (!isNumber) {
      return undefined;
    }

    const isDecimal = value.includes(".");
    if (isDecimal) {
      const floatValue = parseFloat(value);
      return !isNaN(floatValue) ? floatValue : undefined;
    }

    const intValue = parseInt(value);
    return !isNaN(intValue) ? intValue : undefined;
  };

  // isEncodedValueValid is a check for if the value is valid before encoding
  // since the value is a number, it will always be valid, but since we want to
  // check for the metric conversion, we will return false
  override isEncodedValueValid = (value: any): boolean => {
    return false;
  };

  override encode = (value: number) => {
    if (this.metric === "percentage") return value / 100;
    return value;
  };
  override decode = (value: any) => {
    const val = typeof value === "number" ? value : undefined;
    if (this.metric === "percentage" && val !== undefined) return val * 100;
    return val;
  };

  allComparators: DataObjectFieldComparator[] = [
    "equals",
    "not-equals",
    "greater-than",
    "greater-than-or-equal-to",
    "less-than",
    "less-than-or-equal-to",
    "includes",
    "is-known",
    "is-unknown",
  ];
  override isValueValid = (value: any): boolean => {
    return typeof value === "number";
  };
}
